import { atom, selector, selectorFamily } from 'recoil'
import { frontendDisplayedCourseSelector } from '@/atoms/auth'
import {
    GetSourceMaterialTypeStatsResponse,
    SourceMaterialTypeStats,
} from 'common/src/api/contentFrontendVsContentBackend/responses'
import {
    getSourceMaterialTypeStatsForCourse,
    getSourceMaterialTypesForCourse,
} from '@/api/cb/contentCreatorContent'
import { ContentPath, hashContentPath } from 'common/src/ContentPath'

export const sourceMaterialTypeStatsInvalidatorAtom = atom<number>({
    key: 'sourceMaterialTypeStatsInvalidatorAtom',
    default: 0,
})

export const sourceMaterialTypeStatsResponseAtomNonMemoizing =
    atom<GetSourceMaterialTypeStatsResponse>({
        key: 'sourceMaterialTypeStatsResponseAtomNonMemoizing',
        default: selector<GetSourceMaterialTypeStatsResponse>({
            key: 'sourceMaterialTypeStatsResponseAtomNonMemoizingFetcher',
            get: async ({ get }) => {
                get(sourceMaterialTypeStatsInvalidatorAtom)
                return (
                    await getSourceMaterialTypeStatsForCourse({
                        courseName: get(frontendDisplayedCourseSelector),
                    })
                ).data.payload
            },
        }),
    })

export const sourceMaterialTypeStatsAtomNonMemoizing =
    selector<SourceMaterialTypeStats>({
        key: 'sourceMaterialTypeStatsAtomNonMemoizing',
        get: ({ get }) =>
            get(sourceMaterialTypeStatsResponseAtomNonMemoizing)
                .sourceMaterialTypeStats,
    })

// Hashed content paths of every section that has at least one source material
// (drives pre-flight gating of agent surfaces that require source material)
export const hashedContentPathsWithSourceMaterialSelector = selector<
    Set<string>
>({
    key: 'hashedContentPathsWithSourceMaterialSelector',
    get: ({ get }) => {
        const response = get(sourceMaterialTypeStatsResponseAtomNonMemoizing)
        // Prefer the explicit presence list (includes untyped materials the
        // per-type stats skip); fall back to deriving from stats for an older
        // backend that doesn't send it yet
        if (response.hashedContentPathsWithSourceMaterial) {
            return new Set(response.hashedContentPathsWithSourceMaterial)
        }
        const hashedPaths = new Set<string>()
        Object.values(response.sourceMaterialTypeStats).forEach(
            (sourceMaterials) =>
                sourceMaterials.forEach((sourceMaterial) =>
                    hashedPaths.add(hashContentPath(sourceMaterial.contentPath))
                )
        )
        return hashedPaths
    },
})

export const sourceMaterialIDsForTypeSelector = selectorFamily<
    { id: string; contentPath: ContentPath }[],
    string
>({
    key: 'sourceMaterialIDsForTypeSelector',
    get:
        (type) =>
        ({ get }) => {
            return get(sourceMaterialTypeStatsAtomNonMemoizing)[type]
        },
})

export const sourceMaterialTypesInvalidatorAtom = atom<number>({
    key: 'sourceMaterialTypesInvalidatorAtom',
    default: 0,
})
export const sourceMaterialTypesAtom = atom<string[]>({
    key: 'sourceMaterialTypesAtom',
    default: selector<string[]>({
        key: 'sourceMaterialTypesFetcher',
        get: async ({ get }) => {
            get(sourceMaterialTypesInvalidatorAtom)
            return (
                await getSourceMaterialTypesForCourse({
                    courseName: get(frontendDisplayedCourseSelector),
                })
            ).data.payload.sourceMaterialTypes
        },
    }),
})
